import cloneDeep from 'lodash-es/cloneDeep'
import {
  WatermarkConfig,
  WatermarkConfigs,
  UrlForConfig,
  WatermarkRows,
  WatermarkRow,
  Documents,
  Document,
  StatusItems,
  StatusItem,
  RowStatus,
  ConfigStatus,
} from '@/pages/admin/documentTools/types.ts'
import { getFileDownloadUrls } from '@/pages/admin/documentTools/helpers/watermarkRequests'

const ALL_CONFIGS = ['internal', 'text', 'curves', 'lenient', 'im_zero', 'all_x', 'resources']

export const getInternalRow = (url: string): WatermarkConfig => {
  return {
    name: 'internal',
    cleanPdfUrl: url,
    status: ConfigStatus.UNAPPROVED,
  }
}

const getLikelyConfig = (name: string, urlForConfig: UrlForConfig): WatermarkConfig => {
  return {
    name: name,
    cleanPdfUrl: urlForConfig.clean_pdf,
    overlayDiffUrl: urlForConfig['diff-overlay'],
    status: ConfigStatus.UNAPPROVED,
  }
}

export const allProcessesCompleted = (statusItems): boolean => {
  return statusItems.every((item) => item.status === 'success' || item.status === 'failed')
}

export const someProcessesCompleted = (statusItems): boolean => {
  return statusItems.some((item) => item.status === 'success' || item.status === 'failed')
}

export const rowIsIncomplete = (row: WatermarkRow, processIdentifier: string): boolean => {
  return row.status === RowStatus.STARTED && row.processIdentifier === processIdentifier
}

export const getRowIndex = (rows: WatermarkRows, docName: string | undefined): number => {
  if (!docName) {
    return -1
  }
  return rows.findIndex((r: WatermarkRow) => r.documentName === docName)
}

export const getConfigIndex = (configs: WatermarkConfigs, configName: string | undefined): number => {
  if (!configName) {
    return -1
  }
  return configs.findIndex((c) => c.name === configName)
}

export const getInitialWatermarkRows = (documents: Documents): WatermarkRows => {
  return documents.map((doc: Document) => ({
    documentName: doc.document_name,
    likelyConfig: [],
    unlikelyConfig: [],
    noOpConfig: [],
    acceptedConfig: { name: ConfigStatus.UNKNOWN },
    createdAt: null,
    createdBy: null,
    status: RowStatus.STARTED,
    processIdentifier: doc.process_identifier,
  }))
}

const allConfigsComplete = (row: WatermarkRow): boolean => {
  return ALL_CONFIGS.map((configName) => configComplete(row, configName)).every((complete) => complete)
}

const configComplete = (row: WatermarkRow, configName: string): boolean => {
  return (
    row.likelyConfig.some((config) => config.name === configName) ||
    row.unlikelyConfig.includes(configName) ||
    row.noOpConfig.includes(configName)
  )
}

const allConfigsAreNoOp = (row: WatermarkRow): boolean => {
  return row.noOpConfig.length === ALL_CONFIGS.length && row.likelyConfig.length === 0
}

export const getUpdatedStatus = (row: WatermarkRow): string | null => {
  if (allConfigsComplete(row)) {
    return allConfigsAreNoOp(row) ? RowStatus.FAILED : RowStatus.FINISHED
  } else {
    return null
  }
}

const sortConfigs = (watermarkRow: WatermarkRow): WatermarkRow => {
  watermarkRow.likelyConfig.sort((a, b) => {
    if (a.name === 'internal' && b.name !== 'internal') return 1
    if (b.name === 'internal' && a.name !== 'internal') return -1
    return 0
  })
  return watermarkRow
}

export const updateConfig = async ({
  watermarkRow,
  docName,
  statusItems,
}: {
  watermarkRow: WatermarkRow
  docName: string
  statusItems?: StatusItems
}): Promise<WatermarkRow> => {
  const row = cloneDeep(watermarkRow)

  if (!statusItems) {
    const configs = ['text', 'curves', 'lenient', 'im_zero', 'all_x', 'resources']
    configs.forEach((config) => {
      if (!row.noOpConfig.includes(config)) {
        row.noOpConfig.push(config)
      }
    })
  } else {
    const urls = await getFileDownloadUrls(docName)
    row.createdAt = statusItems[0].created_at
    row.createdBy = statusItems[0].created_by

    statusItems.forEach((statusItem: StatusItem) => {
      if (statusItem.status === 'success' || statusItem.status === 'failed') {
        const likelyConfigNames = row.likelyConfig.map((config) => config.name)
        const isUnlikely = statusItem.successful_removal_config?.includes('unlikely')
        const isLikely = !isUnlikely && statusItem.successful_removal_config?.includes('likely')
        const isNoOp = statusItem.successful_removal_config?.includes('no_op')

        if (!row.noOpConfig.includes(statusItem.config) && isNoOp) {
          row.noOpConfig.push(statusItem.config)
        }

        if (!row.unlikelyConfig.includes(statusItem.config) && isUnlikely) {
          row.unlikelyConfig.push(statusItem.config)
        }

        if (!likelyConfigNames.includes(statusItem.config) && isLikely) {
          const urlForConfig = urls.download_urls.find((url) => url.config === statusItem.config)
          row.likelyConfig.push(getLikelyConfig(statusItem.config, urlForConfig))
          row.likelyConfig = sortConfigs(row).likelyConfig
        }
      }
    })
  }
  return row
}

export const updateWatermarkRowStatus = ({ watermarkRow }: { watermarkRow: WatermarkRow }): WatermarkRow => {
  const status = getUpdatedStatus(watermarkRow)
  if (status) {
    watermarkRow.status = status

    const isFailed = status === RowStatus.FAILED
    const isFinished = status === RowStatus.FINISHED
    const noLikelyConfigs = isFinished && watermarkRow.likelyConfig.length === 0

    if (isFailed || (noLikelyConfigs && isFinished)) {
      watermarkRow.acceptedConfig = { name: ConfigStatus.REJECTED }
    } else if (isFinished) {
      watermarkRow.acceptedConfig = { name: ConfigStatus.UNKNOWN }
    }
  }
  return watermarkRow
}

export const updateConfigStatus = ({
  watermarkRows,
  documentName,
  configName,
  status,
}: {
  watermarkRows: WatermarkRows
  documentName: string
  configName: string
  status: ConfigStatus
}): WatermarkRows => {
  const watermarkRowsCopy = cloneDeep(watermarkRows)
  const rowIndex = watermarkRowsCopy.findIndex((row) => row.documentName === documentName)
  if (rowIndex !== -1) {
    if (status === ConfigStatus.APPROVED) {
      watermarkRowsCopy[rowIndex].likelyConfig.map((config) => {
        if (config.status === ConfigStatus.APPROVED) {
          config.status = ConfigStatus.UNAPPROVED
        }
        return config
      })
    }
    const configIndex = watermarkRowsCopy[rowIndex].likelyConfig.findIndex((config) => config.name === configName)
    watermarkRowsCopy[rowIndex].likelyConfig[configIndex].status = status
  }
  return watermarkRowsCopy
}

export const getAcceptedConfig = (row: WatermarkRow): WatermarkConfig | undefined => {
  return row.likelyConfig.find((config) => config.status === ConfigStatus.APPROVED)
}

export const allConfigsRejected = (row: WatermarkRow): boolean => {
  return row.likelyConfig.every((config) => config.status === ConfigStatus.REJECTED) && row.likelyConfig.length > 0
}
